import React, {createContext, PropsWithChildren, useEffect, useMemo, useState} from "react";
import Echo from "laravel-echo";
import Pusher from 'pusher-js';
import {toast} from "react-toastify";
import {useDispatch, useSelector} from "react-redux";
import {useQueryClient} from "@tanstack/react-query";
import {useNavigate} from "react-router-dom";
import {addTrafficOrderEvent} from "../store/notifications/actions";
import { getAuthToken, getAuthUser } from "../helpers/auth";
import { ticketBus } from "../helpers/ticketEventBus";
import {playNotificationSound} from "../helpers/notificationSound";

const getBroadcastApiBase = (): string => {
    const raw = (process.env.REACT_APP_BASE_API_URL || "").trim();
    const base = raw.replace(/\/+$/, "");
    return base ? `${base}/api` : "/api";
};

const buildBroadcastHeaders = (): Record<string, string> => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// Pusher.logToConsole = true

export const MyEcho = new Echo({
    broadcaster: 'pusher',
    Pusher,
    key: 'app-key',
    wsHost: 'soketi.projektsolutions.tech',
    wssHost: 'soketi.projektsolutions.tech',
    wsPort: 443,
    wssPort: 443,
    forceTLS: true,
    encrypted: true,
    cluster: 'mt1',
    enabledTransports: ['ws', 'wss'],
    authEndpoint: `${getBroadcastApiBase()}/broadcasting/auth`,
    authorizer: (channel: any) => ({
        authorize: (socketId: string, callback: (error: Error | null, data: any) => void) => {
            fetch(`${getBroadcastApiBase()}/broadcasting/auth`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Accept': 'application/json',
                    ...buildBroadcastHeaders(),
                },
                body: `socket_id=${encodeURIComponent(socketId)}&channel_name=${encodeURIComponent(channel.name)}`,
            })
                .then(async (res) => {
                    if (!res.ok) {
                        const body = await res.text();
                        throw new Error(`Broadcasting auth failed: ${res.status} ${body}`);
                    }
                    return res.json();
                })
                .then((data) => callback(null, data))
                .catch((err) => callback(err, null));
        },
    }),
})

/*export const MyEcho = new Echo({
    broadcaster: 'pusher',
    Pusher,
    key: 'app-key', // set the key defined in your .env new-lawyer-key
    wsHost: 'localhost', // the host defined in your .env
    wsPort: 6001, // or the port defined in your .env
    forceTLS: false,
    encrypted: true,
    cluster: 'mt1',
    enabledTransports: ['ws', 'wss'],
})*/

export const BroadcastContext = createContext<any>(null);

export const BroadcastProvider = ({children}: PropsWithChildren) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [instance, setInstance] = useState<any>(null);
    const [currentPrivateChannel, setCurrentPrivateChannel] = useState<any>(null);
    const [systemChannel, setSystemChannel] = useState<any>(null);
    const [notifications, setNotifications] = useState<any[]>([]);

    const loginUserId = useSelector((state: any) => state.login.data.user_id || null);
    const authUser = getAuthUser();
    const user_id  = authUser?.user_id || loginUserId;

    const broadcast = {
        instance,
        currentPrivateChannel,
        systemChannel,
        notifications,
    };

    const unsubscribeFromChannels = () => {
        if (user_id) {
            MyEcho.leave(`App.Models.User.${user_id}`);
            console.log(`Отписка от канала: App.Models.User.${user_id}`);
        }
    };

    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);

    useEffect(() => {
        if (user_id) {
            MyEcho.connector.options.auth = {
                ...(MyEcho.connector.options.auth || {}),
                headers: buildBroadcastHeaders(),
            };

            const pusherConnection = MyEcho.connector.pusher.connection;
            let wasDisconnected = false;

            const handleStateChange = (states: { previous: string; current: string }) => {
                if (states.current === 'disconnected' || states.current === 'unavailable' || states.current === 'failed') {
                    wasDisconnected = true;
                    console.warn(`WebSocket disconnected (${states.current})`);
                }

                if (states.current === 'connected' && wasDisconnected) {
                    wasDisconnected = false;
                    console.log('WebSocket reconnected — dispatching silent refresh');
                    ticketBus.emit('ws-reconnected');
                }
            };

            pusherConnection.bind('state_change', handleStateChange);

            const privateChannel = MyEcho.private(`App.Models.User.${user_id}`)
              .subscribed(() => {})
              .listen('.lead.event', (data: any) => {

                  if (!data || !data.data) {
                      console.warn('Пустые данные или поле data не существует:', data);
                      return;
                  }

                  const {data: loadData} = data
                  if (loadData) {
                      playNotificationSound();
                      toast.info(() => (
                        <div>
                            <strong>User Information: </strong>
                            <ul>
                                {Object.entries(loadData).map(([key, value]: [string, any], index: number) => (
                                  <li key={index}> {key}: {value}  </li>
                                ))}
                            </ul>
                        </div>
                      ), {
                          closeOnClick: true,
                          pauseOnHover: true,
                          draggable: true,
                          autoClose: false,
                      });
                  }
              })
              .listen('.traffic.order.event', (data: any) => {
                  const { data: result } = data
                  dispatch(addTrafficOrderEvent(result));
                    toast.info(() => (
                      <div onClick={() => navigate('/traffic-orders')}>
                          <strong>Traffic Order Information: </strong>
                          <div> {result.message}</div>
                      </div>
                    ), {
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        autoClose: 1500,
                        theme: localStorage.getItem("layoutMode") || "light",
                    });

                  result.keys.forEach((key: string) => {
                      queryClient.invalidateQueries({ queryKey: [key] });
                  });
              })
              .listen('.ticket.accepted.event', (data: any) => {
                  const { data: result } = data;
                  if (!result) {
                      return;
                  }

                  ticketBus.emit('ticket-accepted', result);

                  playNotificationSound();
                  toast.info(() => (
                    <div onClick={() => navigate(`/tickets/${result.ticket_uuid}`)}>
                        <strong>Ticket accepted:</strong>
                        <div>{result.message}</div>
                        <small>{result.ticket_subject} · {result.accepted_by_name}</small>
                    </div>
                  ), {
                      closeOnClick: true,
                      pauseOnHover: true,
                      draggable: true,
                      autoClose: 5000,
                      theme: localStorage.getItem("layoutMode") || "light",
                  });
              })
              .listen('.ticket.created.event', (data: any) => {
                  const { data: result } = data;
                  if (!result?.ticket_uuid) {
                      return;
                  }

                  if (Number(result?.created_by_user_id) === Number(user_id)) {
                      return;
                  }

                  ticketBus.emit('ticket-created', result);

                  playNotificationSound();

                  if ('Notification' in window && Notification.permission === 'granted') {
                      try {
                          const notification = new Notification('New ticket', {
                              body: `${result.ticket_subject || ''}\nby ${result.created_by_name || ''}`,
                              tag: `ticket-created-${result.ticket_id}`,
                          });
                          notification.onclick = () => {
                              window.focus();
                              navigate(`/tickets/${result.ticket_uuid}`);
                              notification.close();
                          };
                      } catch (e) {
                          console.error('Browser Notification error:', e);
                      }
                  }

                  toast.info(() => (
                    <div onClick={() => navigate(`/tickets/${result.ticket_uuid}`)}>
                        <strong>New ticket</strong>
                        <div>{result.message}</div>
                        <small>{result.ticket_subject} · by {result.created_by_name}</small>
                    </div>
                  ), {
                      closeOnClick: true,
                      pauseOnHover: true,
                      draggable: true,
                      autoClose: 5000,
                      theme: localStorage.getItem("layoutMode") || "light",
                  });
              })
              .listen('.ticket.message.created.event', (data: any) => {
                  const { data: result } = data;
                  if (!result?.ticket_uuid || !result?.message) {
                      return;
                  }

                  ticketBus.emit('ticket-message-created', result);

                  playNotificationSound();
                  toast.info(() => (
                    <div onClick={() => navigate(`/tickets/${result.ticket_uuid}`)}>
                        <strong>New ticket message</strong>
                        <div>Ticket #{result.ticket_id}</div>
                    </div>
                  ), {
                      closeOnClick: true,
                      pauseOnHover: true,
                      draggable: true,
                      autoClose: 4000,
                      theme: localStorage.getItem("layoutMode") || "light",
                  });
              })
              .listen('.ticket.updated.event', (data: any) => {
                  const { data: result } = data;
                  if (!result?.ticket_uuid || !result?.message) {
                      return;
                  }

                  ticketBus.emit('ticket-updated', result);

                  playNotificationSound();
                  toast.info(() => (
                    <div onClick={() => navigate(`/tickets/${result.ticket_uuid}`)}>
                        <strong>Ticket updated</strong>
                        <div>{result.message}</div>
                        <small>{result.ticket_subject} · by {result.changed_by_name}</small>
                    </div>
                  ), {
                      closeOnClick: true,
                      pauseOnHover: true,
                      draggable: true,
                      autoClose: 5000,
                      theme: localStorage.getItem("layoutMode") || "light",
                  });
              })
              .listen('.ticket.deleted.event', (data: any) => {
                  const { data: result } = data;
                  if (!result?.ticket_uuid) {
                      return;
                  }

                  ticketBus.emit('ticket-deleted', result);

                  playNotificationSound();
                  toast.warning(() => (
                    <div>
                        <strong>Ticket deleted</strong>
                        <div>{result.message}</div>
                    </div>
                  ), {
                      closeOnClick: true,
                      pauseOnHover: true,
                      draggable: true,
                      autoClose: 5000,
                      theme: localStorage.getItem("layoutMode") || "light",
                  });
              });
            setCurrentPrivateChannel(privateChannel);

            /*return () => {
                MyEcho.leaveChannel(`App.Models.User.${user_id}`);
            };*/
            return () => {
                setCurrentPrivateChannel(null);
                pusherConnection.unbind('state_change', handleStateChange);
                unsubscribeFromChannels();
            };
        }
    }, [user_id]);

    return (
        <BroadcastContext.Provider value={{ ...broadcast, unsubscribeFromChannels }}>
            {children}
        </BroadcastContext.Provider>
    );
}
